/** Org-wide share sentinel (matches Drive page + modal + backend audience rules). */
export const ORG_SHARE_TARGET = "__ORG__";

/**
 * Same normalization as Drive quick-add / folder-sync `matchFolderKey`
 * so localStorage prefs align with sync API calls.
 */
export const sanitizeDriveFolderMatchKey = (folderName: string): string =>
  folderName.replace(/[/\\]/g, "-").replace(/\s+/g, " ").trim();

/** True when the name matches a folder already shared in the given option list. */
export const isExistingDriveFolderInOptions = (
  folderName: string,
  existingFolderOptions: string[],
): boolean => {
  const key = sanitizeDriveFolderMatchKey(folderName);
  if (!key) return false;
  return existingFolderOptions.some(
    (opt) => sanitizeDriveFolderMatchKey(opt) === key,
  );
};

/** True when any circle/org already has a Drive folder with this name. */
export const isExistingDriveFolderInCatalog = (
  folderName: string,
  foldersByAudience: Map<string, Set<string>>,
): boolean => {
  const key = sanitizeDriveFolderMatchKey(folderName);
  if (!key) return false;
  return Array.from(foldersByAudience.values()).some((names) =>
    Array.from(names).some(
      (name) => sanitizeDriveFolderMatchKey(name) === key,
    ),
  );
};

/** Audience (circle id or `ORG_SHARE_TARGET`) that owns this folder, if unambiguous. */
export const resolveDriveFolderAudienceKey = (
  folderName: string,
  notices: Array<{ targetSubcircle?: string; title?: string; attachments?: Array<{ name?: string }> }> | undefined,
  preferredAudienceKey?: string,
): string | null => {
  if (!notices?.length) return null;
  const targetKey = sanitizeDriveFolderMatchKey(folderName);
  if (!targetKey) return null;

  const audienceKeys = new Set<string>();
  for (const notice of notices) {
    const backingFolder = getDriveFolderKey(notice.title, undefined);
    if (
      backingFolder &&
      sanitizeDriveFolderMatchKey(backingFolder) === targetKey
    ) {
      audienceKeys.add(getAudienceKey(notice.targetSubcircle));
      continue;
    }
    if (!notice.attachments?.length) continue;
    const inFolder = notice.attachments.some((attachment) => {
      const folder = getDriveFolderKey(notice.title, attachment.name);
      return (
        folder != null && sanitizeDriveFolderMatchKey(folder) === targetKey
      );
    });
    if (inFolder) {
      audienceKeys.add(getAudienceKey(notice.targetSubcircle));
    }
  }

  if (audienceKeys.size === 0) return null;
  const keys = Array.from(audienceKeys);
  if (keys.length === 1) return keys[0];
  if (preferredAudienceKey && audienceKeys.has(preferredAudienceKey)) {
    return preferredAudienceKey;
  }
  return keys.sort((a, b) => a.localeCompare(b))[0];
};

export const FOLDER_SELECTION_SEPARATOR = "::";

export const getFolderNameFromAttachmentName = (
  name: string | undefined,
): string | null => {
  if (!name) return null;
  const parts = name.replace(/\\/g, "/").split("/").filter((p) => p.length > 0);
  if (parts.length < 2) return null;
  const folderPath = parts.slice(0, -1).join("/").trim();
  return folderPath || null;
};

export const getDriveFolderKey = (
  noticeTitle: string | undefined,
  attachmentName: string | undefined,
): string | null => {
  const fromPath = getFolderNameFromAttachmentName(attachmentName);
  if (fromPath) return fromPath;
  if (!noticeTitle) return null;
  const m = noticeTitle.trim().match(/^Drive folder:\s*(.+)$/i);
  return m ? m[1].trim() || null : null;
};

/** True for synthetic notices that back a Drive folder (title `Drive folder: …`). */
export const isDriveFolderBackingNotice = (
  noticeTitle: string | undefined,
): boolean => getDriveFolderKey(noticeTitle, undefined) != null;

/** Map dashboard uploaded-file payloads to Drive folder-sync attachment rows */
export const toDriveFolderSyncAttachments = (
  files: Array<{
    fileUrl: string;
    fileName: string;
    contentType: string;
    size: number;
  }>,
) =>
  files.map((f) => ({
    name: f.fileName,
    url: f.fileUrl,
    type: f.contentType || "application/octet-stream",
    size: f.size || 0,
  }));

export const getDisplayFileName = (name: string): string => {
  const idx = name.lastIndexOf("/");
  if (idx < 0) return name;
  return name.slice(idx + 1) || name;
};

export const getAudienceKey = (targetSubcircle?: string) =>
  targetSubcircle || ORG_SHARE_TARGET;

/** Maps notice composer audience fields to the Drive folder audience key. */
export const resolveNoticeAudienceKey = (
  audience: string | undefined,
  targetSubcircle?: string,
): string => {
  if (audience === "subcircle" && targetSubcircle?.trim()) {
    return targetSubcircle.trim();
  }
  return ORG_SHARE_TARGET;
};

/** True once the composer has a concrete send target (org-wide or subcircle). */
export const isNoticeComposerAudienceSelected = (
  audience?: string,
  targetSubcircle?: string,
  staffOnly?: boolean,
): boolean => {
  if (staffOnly) return true;
  if (audience === "circle") return true;
  if (audience === "subcircle" && targetSubcircle?.trim()) return true;
  return false;
};

type DriveFolderNoticeRef = {
  title?: string;
  targetSubcircle?: string;
  isDeleted?: boolean;
  isDraft?: boolean;
  driveFolderNotifyOnAdd?: boolean;
  attachments?: Array<{ name?: string }> | null;
};

/** Folder names already shared for the given circle/org audience. */
export const getDriveFolderNamesForAudience = (
  notices: DriveFolderNoticeRef[] | undefined,
  audienceKey: string,
): string[] => {
  const names = new Set<string>();
  for (const notice of notices || []) {
    if (notice.isDeleted || notice.isDraft) continue;
    if (getAudienceKey(notice.targetSubcircle) !== audienceKey) continue;
    const fromTitle = getDriveFolderKey(notice.title, undefined);
    if (fromTitle) names.add(fromTitle);
    for (const attachment of notice.attachments || []) {
      const fromPath = getDriveFolderKey(notice.title, attachment.name);
      if (fromPath) names.add(fromPath);
    }
  }
  return Array.from(names).sort((a, b) => a.localeCompare(b));
};

export const buildFolderSelectionKey = (folderName: string, audienceKey: string) =>
  `${audienceKey}${FOLDER_SELECTION_SEPARATOR}${folderName}`;

export const parseFolderSelectionKey = (
  value: string,
): { audienceKey: string | null; folderName: string } => {
  const separatorIndex = value.indexOf(FOLDER_SELECTION_SEPARATOR);
  if (separatorIndex < 0) {
    return { audienceKey: null, folderName: value };
  }
  return {
    audienceKey: value.slice(0, separatorIndex) || null,
    folderName: value.slice(separatorIndex + FOLDER_SELECTION_SEPARATOR.length),
  };
};

/**
 * Card title (top line): the folder path segment only.
 * When the stored folder name repeats the circle (e.g. "Little Lights/Books"),
 * strip the circle prefix so the circle row below is not duplicated.
 */

/** True when a notice belongs to the given Drive folder for the given audience. */
export const noticeBelongsToDriveFolderAudience = (
  notice: DriveFolderNoticeRef,
  folderName: string,
  audienceKey: string,
): boolean => {
  if (notice.isDeleted) return false;
  if (getAudienceKey(notice.targetSubcircle) !== audienceKey) return false;
  const sanitizedTarget = sanitizeDriveFolderMatchKey(folderName);
  if (!sanitizedTarget) return false;
  const titleFolder = getDriveFolderKey(notice.title, undefined);
  if (
    titleFolder &&
    sanitizeDriveFolderMatchKey(titleFolder) === sanitizedTarget
  ) {
    return true;
  }
  return (notice.attachments || []).some((attachment) => {
    const fk = getDriveFolderKey(notice.title, attachment.name);
    return fk != null && sanitizeDriveFolderMatchKey(fk) === sanitizedTarget;
  });
};

/** Server-backed default: notify on add unless backing notice has driveFolderNotifyOnAdd === false. */
export const resolveDriveFolderNotifyOnAdd = (
  notices: DriveFolderNoticeRef[] | undefined,
  folderName: string,
  audienceKey: string,
): boolean => {
  const sanitizedTarget = sanitizeDriveFolderMatchKey(folderName);
  if (!sanitizedTarget || !notices?.length) return true;
  const matches = notices.filter((notice) =>
    noticeBelongsToDriveFolderAudience(notice, sanitizedTarget, audienceKey),
  );
  const canonical =
    matches.find(
      (notice) => getDriveFolderKey(notice.title, undefined) != null,
    ) || matches[0];
  return canonical?.driveFolderNotifyOnAdd !== false;
};

export const getFolderCardTitle = (folderName: string, circleName: string): string => {
  const name = folderName.trim();
  const circle = circleName.trim();
  if (!name || !circle || name.length <= circle.length + 1) {
    return name;
  }
  if (
    name.charAt(circle.length) === "/" &&
    name.slice(0, circle.length).toLowerCase() === circle.toLowerCase()
  ) {
    const rest = name.slice(circle.length + 1).trim();
    return rest || name;
  }
  return name;
};

export const isDriveImageFile = (type?: string, name?: string): boolean => {
  if (type && type.startsWith("image/")) return true;
  if (name) {
    const ext = name.toLowerCase().split(".").pop();
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
    return imageExts.includes(ext || "");
  }
  return false;
};

export const isDrivePdfFile = (type: string, name?: string): boolean => {
  if (type === "application/pdf") return true;
  if (name) {
    return name.toLowerCase().endsWith(".pdf");
  }
  return false;
};

export const isDriveVideoFile = (type?: string, name?: string): boolean => {
  if (type && type.startsWith("video/")) return true;
  if (name) {
    const lower = name.toLowerCase();
    return (
      lower.endsWith(".mp4") ||
      lower.endsWith(".mov") ||
      lower.endsWith(".mpeg") ||
      lower.endsWith(".avi") ||
      lower.endsWith(".wmv") ||
      lower.endsWith(".3gp")
    );
  }
  return false;
};

export const formatDriveFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDriveRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return "Today";
  }
  if (diffInDays === 1) {
    return "Yesterday";
  }
  if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};
